import ApiService from './ApiService';

class EmailGeneratorService {
  constructor() {
    this.apiService = new ApiService(process.env.REACT_APP_EMAIL_GENERATOR_URL || 'http://localhost:8001');
  }

  async generateEmails(requestData, idToken) {
    const response = await this.apiService.post('/generate-emails/', requestData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    return response.data;
  }

  /**
   * Send a support message
   * @param {string} message - The support message content
   * @param {string} userEmail - The email of the user sending the message
   * @returns {Promise<Object>} - Response from the support message endpoint
   */
  async sendSupportMessage(message, userEmail) {
    try {
      const requestData = {
        message,
        userEmail,
        timestamp: new Date().toISOString()
      };

      const response = await this.apiService.post('/support-message/', requestData);
      
      if (!response || !response.success) {
        throw new Error('Failed to send support message');
      }

      return response;
    } catch (error) {
      console.error('Error sending support message:', error);
      throw error;
    }
  }

  /**
   * Send a VIP search notification to founder
   * @param {string} userEmail - The email of the user who triggered VIP search
   * @param {string} searchQuery - The search query that returned no results
   * @returns {Promise<Object>} - Response from the VIP notification endpoint
   */
  async sendVipSearchNotification(userEmail, searchQuery) {
    try {
      const requestData = {
        userEmail,
        searchQuery,
        timestamp: new Date().toISOString(),
        type: 'vip_search_notification'
      };

      // Call the BigQuery generator endpoint instead of email generator
      const response = await fetch(`${process.env.REACT_APP_BIG_QUERY_URL}/vip-search-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await this.getAuthToken()}`
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result || !result.status === 'success') {
        throw new Error('Failed to send VIP search notification');
      }

      return result;
    } catch (error) {
      console.error('Error sending VIP search notification:', error);
      throw error;
    }
  }

  /**
   * Get Firebase auth token for API calls
   * @returns {Promise<string>} - Firebase auth token
   */
  async getAuthToken() {
    try {
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      return await currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  }

  async generateEmailAddresses(addressRequestData, idToken) {
    const response = await this.apiService.post('/generate-email-addresses/', addressRequestData, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    return response.data;
  }

  async sendEmails(emailData, gmailToken, idToken) {
    const response = await this.apiService.post('/send-emails', { emails: emailData, gmailToken }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    return response.data;
  }
}

// Create and export a single instance
const emailGeneratorService = new EmailGeneratorService();
export default emailGeneratorService;
